import PropTypes from 'prop-types';
import { Box, CloseButton, Flex, Icon, Link, useColorModeValue } from '@chakra-ui/react';
import { FiFileText, FiLayout, FiSearch, FiTag, FiMap, FiTool, FiList, FiCheckSquare } from 'react-icons/fi';
import { BiBuildings, BiCloudUpload, BiCalculator, BiUserCircle, BiData, BiLink, BiErrorAlt } from 'react-icons/bi';
import { MdOutlineFolderSpecial, MdPersonSearch, MdOutlineWebhook } from 'react-icons/md';
import { NavLink as RouterLink } from 'react-router-dom';
import { TbCertificate, TbListSearch } from 'react-icons/tb';
import { IoNotifications } from 'react-icons/io5';
import triageLogo from '/triage.svg';
import { useSelector } from 'react-redux';
import { selectUserAdmin, selectUserRoles, selectUserSortBy, selectUserSuperadmin, selectUserUsername } from '../user/userSlice';
import { selectContractSortBy, selectContractSortDirection } from '../contract/contractSlice';
import { selectModalitySortBy, selectModalitySortDirection } from '../modality/modalitySlice';
import { LiaSuitcaseSolid, LiaUsersSolid } from 'react-icons/lia';

const viteHost = import.meta.env.VITE_HOST;

const hasPermissions = (link, userRoles, admin) => {
  return (link.roles && link.roles.length > 0 && link.roles.some((r) => userRoles?.includes(r))) || admin || !link.roles;
};

const SidebarContent = ({ onClose, isMobile }) => {
  const white = useColorModeValue('gray.100', 'gray.900');
  const gray = useColorModeValue('gray.300', 'gray.700');
  const roles = useSelector(selectUserRoles);
  const admin = useSelector(selectUserAdmin);
  const superadmin = useSelector(selectUserSuperadmin);
  const name = useSelector(selectUserUsername);
  const sortBy = useSelector(selectContractSortBy);
  const sortDirection = useSelector(selectContractSortDirection);
  const authUserSortBy = useSelector(selectUserSortBy);
  const authUserSortDirection = useSelector(selectContractSortDirection);
  const modalitySortBy = useSelector(selectModalitySortBy);
  const modalitySortDirection = useSelector(selectModalitySortDirection);

  const linkItems = [
    { name: 'Pages', heading: true, border: false },
    {
      name: 'Contracts',
      icon: FiFileText,
      href: `/contracts?sortBy=${sortBy}&sortDirection=${sortDirection}`,
      target: '_self',
      roles: ['contracts'],
    },
    { name: 'Job Loader', icon: BiCloudUpload, href: '/job_loader', target: '_self', roles: ['jobs'] },
    { name: 'Margin Rules', icon: BiCalculator, admin: true, href: '/margin_rules', target: '_self' },
    { name: 'Facility Mapping', icon: BiBuildings, roles: ['jobs'], href: '/facility_mapping', target: '_self' },
    { name: 'Specialty Mapping', icon: MdOutlineFolderSpecial, roles: ['jobs'], href: '/specialty_mapping', target: '_self' },
    { name: 'Jump Links', icon: BiLink, roles: ['support'], href: '/jump_links', target: '_self' },
    { name: 'Entity Lookup', icon: MdPersonSearch, href: '/entity_lookup', superadmin: false, roles: ['support'] },
    { name: 'Certifications', icon: TbCertificate, href: '/certifications', target: '_self', roles: ['support'] },
    { name: 'Search Data Dumps', icon: BiData, href: '/data_dumps', target: '_self' },
    { name: 'Job Closing Rules', icon: LiaSuitcaseSolid, href: '/job_closing_rules', target: '_self', roles: ['support'] },
    { name: 'Equipment', icon: FiTool, href: '/equipment', target: '_self', roles: ['support'] },
    { name: 'Skills', icon: FiCheckSquare, href: '/skills', target: '_self', roles: ['support', 'skills'] },
    { name: 'Form Builder', icon: FiList, href: '/forms', target: '_self', roles: ['support'] },
    { name: 'Job Feed', icon: MdOutlineWebhook, href: '/job_feed', target: '_self', roles: ['support', 'marketing'] },
    { name: 'Administration', heading: true, admin: true, border: true },
    { name: 'Feature Management', icon: FiLayout, href: '/feature_management', superadmin: true },
    {
      name: 'Alert Subscription',
      icon: LiaUsersSolid,
      href: '/alert_subscriptions',
      target: '_self',
      admin: true,
    },
    {
      name: 'User Management',
      icon: BiUserCircle,
      href: `/user_management?sortBy=${authUserSortBy}&sortDirection=${authUserSortDirection}`,
      target: '_self',
      admin: true,
    },
    {
      name: 'Modalities',
      icon: FiTag,
      href: `/modalities?sortBy=${modalitySortBy}&sortDirection=${modalitySortDirection}`,
      target: '_self',
      admin: true,
    },
    { name: 'States', icon: FiMap, admin: true, href: '/states', target: '_self' },
    { name: 'App Configuration', heading: true, roles: ['marketing'], border: true },
    { name: 'Banner', icon: IoNotifications, href: '/banner', target: '_self', roles: ['marketing'] },
    { name: 'Troubleshooting', heading: true, border: true },
    { name: 'Get Tracer Errors', icon: BiErrorAlt, href: `${viteHost}/monitoring/redis/tracers/errors`, target: '_blank' },
    { name: 'Search Tracers', icon: FiSearch, href: '/tracers' },
    { name: 'Monitor Consumer Groups', icon: TbListSearch, href: '/monitoring', target: '_self', roles: ['support'] },
  ];

  return (
    <Box
      bg={white}
      borderRightWidth="1px"
      borderRightColor={gray}
      w={{ base: 'full', md: 64 }}
      pos="fixed"
      h="full"
      display={isMobile ? { base: 'block', md: 'none' } : { base: 'none', md: 'block' }}
      zIndex={2}
      overflowY="scroll"
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Box>
          <Link as={RouterLink} to="/">
            <img src={triageLogo} className="logo" alt="Triage logo" width={200} />
          </Link>
        </Box>
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {name !== '' &&
        linkItems.map((link) => {
          if ((link?.admin && !admin) || (link?.superadmin && !superadmin)) {
            return null;
          }
          if (link?.heading && hasPermissions(link, roles, admin)) {
            return (
              <Box
                key={link.name}
                pt={3}
                px={6}
                color="brand.900"
                fontSize="md"
                my="2"
                fontWeight="bold"
                borderTopColor={gray}
                borderTopWidth={link.border ? '1px' : '0'}
              >
                {link.name}
              </Box>
            );
          }
          if (hasPermissions(link, roles, admin)) {
            return (
              <NavItem key={link.name} icon={link.icon} href={link.href} target={link.target}>
                {link.name}
              </NavItem>
            );
          }
        })}
    </Box>
  );
};

SidebarContent.propTypes = {
  onClose: PropTypes.func,
  isMobile: PropTypes.bool,
};

const NavItem = ({ icon, href, target, children }) => {
  return (
    <Link
      as={RouterLink}
      to={href || '#'}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}
      target={target || '_self'}
      _activeLink={{ color: 'brand.700' }}
    >
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        _hover={{
          bg: 'brand.700',
          color: 'white',
        }}
      >
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            _groupHover={{
              color: 'white',
            }}
            as={icon}
          />
        )}
        {children}
      </Flex>
    </Link>
  );
};

NavItem.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  href: PropTypes.string,
  target: PropTypes.string,
  children: PropTypes.node,
};

export default SidebarContent;
